import React from 'react'
import { api, type Review } from '../api/client'
import type { ClientDetail } from '../types/app'
import { stars } from './settings/reviewForm'
import { saveErrorMessage } from './settings/toast'
import Field from './settings/Field'
import { REVIEW_ASKING, reviewAsking, reviewAskingPatch, type ReviewAsking } from './reviewEmails'

interface Props {
  client: ClientDetail
  onClientChange: (client: ClientDetail) => void
}

// ClientReviews is the profile section: whether this client may be emailed a
// review request, and what they have said.
export default function ClientReviews({ client, onClientChange }: Props) {
  const [reviews, setReviews] = React.useState<Review[] | null>(null)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState('')
  const [importedEmails, setImportedEmails] = React.useState(false)

  React.useEffect(() => {
    setReviews(null)
    api.clientReviews(client.id).then(setReviews).catch(() => setReviews([]))
  }, [client.id])

  React.useEffect(() => {
    api.getBusiness().then((b) => setImportedEmails(Boolean(b?.imported_reminders))).catch(() => {})
  }, [])

  const asking = reviewAsking(client)
  const choose = async (choice: ReviewAsking) => {
    if (choice === asking || choice === 'online') return
    setSaving(true)
    setError('')
    try {
      onClientChange(await api.updateClient(client.id, reviewAskingPatch(choice)))
    } catch (err) {
      setError(saveErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="profile-section" aria-label="Reviews">
      <h2>Reviews</h2>
      <Field id="review-asking" label="Ask for a review" error={error || undefined} hint={
        asking === 'online'
          ? importedEmails
            ? 'Asked after online bookings, where they see the review notice, and after imported bookings, since you’ve switched on emails for those. Choose "After every appointment" only if you’ve told them yourself.'
            : 'They saw the review notice when booking online. Choose "After every appointment" only if you’ve told them yourself.'
          : asking === 'always'
            ? 'Asked after every appointment, at most once every 90 days.'
            : 'Never asked for a review.'
      }>
        <select id="review-asking" value={asking} disabled={saving} onChange={(e) => choose(e.target.value as ReviewAsking)}>
          {REVIEW_ASKING(asking, importedEmails).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      {reviews && reviews.length === 0 && <p className="field-hint">No reviews yet</p>}
      {reviews?.map((r) => (
        <article key={r.id} className="review-item">
          <span className="review-item__stars" role="img" aria-label={r.rating ? `${r.rating} out of 5` : 'No rating'}>{stars(r.rating)}</span>
          {r.body && <p>{r.body}</p>}
          <p className="review-item__meta">{r.reviewed_on}</p>
          {r.reply && <p className="review-item__reply">Your reply: {r.reply}</p>}
        </article>
      ))}
    </section>
  )
}
